import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fromScaled, toScaled } from '@loot/shared';
import {
  api,
  type CashProjection,
  type CashSettingsEntity,
  type CashSnapshot,
  type ProjectionItem,
  type ProjectionTotal,
} from '../../api.ts';
import { changed, FormModal, FormSection } from '../../components/FormModal.tsx';
import { ListToolbar } from '../../components/ListFilters.tsx';
import {
  Badge,
  Button,
  EmptyRow,
  ErrorBox,
  Field,
  Input,
  Loading,
  NumCell,
  Panel,
  Select,
  StatCard,
  Table,
} from '../../components/ui.tsx';
import { normalizeDecimal } from '../fpa/expense-form.ts';
import { useFormat } from '../purchasing/common.tsx';
import { CurrencySelect } from '../../components/forms/CurrencySelect.tsx';

const HORIZONS = [7, 15, 30, 60, 90, 180, 365] as const;
const KEY = ['finance', 'treasury', 'projection'];

type Tone = 'neutral' | 'ok' | 'off' | 'info' | 'accent';
const BASIS_TONE: Record<ProjectionItem['basis'], Tone> = {
  overdue: 'off',
  scheduled: 'ok',
  due: 'neutral',
  forecast: 'info',
  expected: 'accent',
  estimate: 'neutral',
};

/**
 * Fluxo diário (ADR 0084, Entrega 3): o caixa projetado dia a dia a partir do saldo realizado de
 * hoje e dos títulos a pagar aprovados, por moeda (moedas nunca se somam). Sem o saldo mínimo da
 * empresa (D-18), a tela pede o cadastro em vez de mostrar a projeção.
 */
export function TreasuryFlow() {
  const { t } = useTranslation();
  const [entityId, setEntityId] = useState('');
  const [days, setDays] = useState<number>(30);
  const [editing, setEditing] = useState(false);
  const settings = useQuery({
    queryKey: [...KEY, 'settings'],
    queryFn: () => api.get<{ entities: CashSettingsEntity[] }>('/finance/treasury/settings'),
  });
  if (settings.isPending) return <Loading />;
  if (settings.isError) return <ErrorBox error={settings.error} />;
  const entities = settings.data.entities;
  if (entities.length === 0) {
    return (
      <Panel>
        <p className="text-sm text-muted">{t('finance.flow.noEntities')}</p>
      </Panel>
    );
  }
  const entity = entities.find((e) => e.legalEntityId === entityId) ?? entities[0]!;
  return (
    <div className="flex flex-col gap-6">
      <ListToolbar
        action={
          entity.canManage &&
          entity.settings && (
            <Button variant="secondary" onClick={() => setEditing(true)}>
              {t('finance.flow.parameters')}
            </Button>
          )
        }
      >
        <Select
          id="flow-entity"
          aria-label={t('finance.flow.fields.entity')}
          value={entity.legalEntityId}
          onChange={(e) => setEntityId(e.target.value)}
        >
          {entities.map((e) => (
            <option key={e.legalEntityId} value={e.legalEntityId}>
              {e.code} · {e.name}
            </option>
          ))}
        </Select>
        <Select
          id="flow-horizon"
          aria-label={t('finance.flow.fields.horizon')}
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
        >
          {HORIZONS.map((h) => (
            <option key={h} value={h}>
              {t('finance.flow.nextDays', { count: h })}
            </option>
          ))}
        </Select>
      </ListToolbar>
      {entity.settings ? (
        <Projection entity={entity} days={days} />
      ) : (
        <Panel title={t('finance.flow.missingTitle')}>
          <p className="text-sm text-muted">
            {entity.canManage ? t('finance.flow.missingCanManage') : t('finance.flow.missingAsk')}
          </p>
          {entity.canManage && (
            <div>
              <Button onClick={() => setEditing(true)}>{t('finance.flow.defineMinimum')}</Button>
            </div>
          )}
        </Panel>
      )}
      {editing && <SettingsForm entity={entity} onDone={() => setEditing(false)} />}
    </div>
  );
}

function Projection({ entity, days }: { entity: CashSettingsEntity; days: number }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const qc = useQueryClient();
  const [open, setOpen] = useState<string | null>(null);
  const [onlyMoving, setOnlyMoving] = useState(true);
  const [target, setTarget] = useState(entity.settings?.consolidationCurrency ?? 'BRL');
  const data = useQuery({
    queryKey: [...KEY, entity.legalEntityId, days, target],
    queryFn: () =>
      api.get<CashProjection>(
        `/finance/treasury/projection?legalEntityId=${entity.legalEntityId}&days=${days}&currency=${target}`,
      ),
  });
  const snapshot = useMutation({
    mutationFn: () =>
      api.post<{ id: string }>('/finance/treasury/projection/snapshots', {
        legalEntityId: entity.legalEntityId,
        days,
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: [...KEY, 'snapshots', entity.legalEntityId] });
    },
  });
  if (data.isPending) return <Loading />;
  if (data.isError) return <ErrorBox error={data.error} />;
  const p = data.data;
  const accountName = new Map(p.accounts.map((a) => [a.accountKey, a.nickname]));
  const withoutBalance = p.accounts.filter((a) => !a.openingDate);
  const currencies = [...new Set(p.totals.map((x) => x.currency))];
  return (
    <>
      {!p.calendarAssigned && <p className="text-sm text-muted">{t('finance.flow.noCalendar')}</p>}
      {withoutBalance.length > 0 && (
        <p className="text-sm text-muted">
          {t('finance.flow.withoutBalance', {
            accounts: withoutBalance.map((a) => a.nickname).join(', '),
          })}
        </p>
      )}
      {currencies.length === 0 && (
        <Panel>
          <p className="text-sm text-muted">{t('finance.flow.nothing')}</p>
        </Panel>
      )}
      {(currencies.length > 1 || (currencies.length === 1 && currencies[0] !== target)) && (
        <Consolidated
          projection={p}
          currencies={currencies}
          target={target}
          onTarget={setTarget}
          onlyMoving={onlyMoving}
        />
      )}
      {currencies.map((currency) => {
        const rows = p.totals.filter((x) => x.currency === currency);
        const summary = p.summaries.find((s) => s.currency === currency)!;
        const today = rows[0]!;
        const money = (v: string | null) => fmt.money(v, currency);
        const moving = (r: ProjectionTotal) =>
          r.date === p.asOf || r.inflows !== '0.0000' || r.outflows !== '0.0000';
        const visible = onlyMoving ? rows.filter(moving) : rows;
        const itemsOf = (date: string) =>
          p.items.filter((i) => i.currency === currency && i.projectedDate === date);
        return (
          <Panel key={currency} title={t('finance.flow.currencyTitle', { currency })}>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard label={t('finance.flow.todayBalance')} value={money(today.closing)} />
              <StatCard
                label={t('finance.flow.lowest')}
                value={money(summary.lowest.closing)}
                detail={fmt.date(summary.lowest.date)}
              />
              <StatCard
                label={t('finance.flow.firstBelowMinimum')}
                value={summary.firstBelowMinimum ? fmt.date(summary.firstBelowMinimum) : '—'}
                detail={
                  p.minimum.currency === currency
                    ? t('finance.flow.minimumIs', { amount: fmt.money(p.minimum.amount, currency) })
                    : undefined
                }
              />
              <StatCard
                label={t('finance.flow.overdueToday')}
                value={today.overdue === '0.0000' ? '—' : money(today.overdue)}
                detail={
                  summary.firstNegative
                    ? t('finance.flow.firstNegative', { date: fmt.date(summary.firstNegative) })
                    : undefined
                }
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={onlyMoving}
                onChange={(e) => setOnlyMoving(e.target.checked)}
              />
              {t('finance.flow.onlyMoving')}
            </label>
            <Table
              head={[
                t('finance.flow.fields.date'),
                { label: t('finance.flow.fields.opening'), align: 'right' },
                { label: t('finance.flow.fields.inflows'), align: 'right' },
                { label: t('finance.flow.fields.outflows'), align: 'right' },
                { label: t('finance.flow.fields.closing'), align: 'right' },
                t('finance.flow.fields.situation'),
              ]}
            >
              {visible.length === 0 && <EmptyRow colSpan={6}>{t('finance.flow.nothing')}</EmptyRow>}
              {visible.map((r) => {
                const index = rows.indexOf(r);
                // Primeiro dia: o saldo realizado de hoje, antes dos compromissos do dia.
                const opening = index > 0 ? rows[index - 1]!.closing : openingOfFirst(r);
                const items = itemsOf(r.date);
                const key = `${currency}|${r.date}`;
                return (
                  <Fragment key={key}>
                    <tr>
                      <td className="whitespace-nowrap">
                        {items.length > 0 ? (
                          <button
                            type="button"
                            className="text-left underline decoration-dotted"
                            aria-expanded={open === key}
                            onClick={() => setOpen(open === key ? null : key)}
                          >
                            {fmt.date(r.date)}
                          </button>
                        ) : (
                          fmt.date(r.date)
                        )}
                        {r.date === p.asOf && (
                          <div className="text-xs text-muted">{t('finance.flow.today')}</div>
                        )}
                      </td>
                      <NumCell>{money(opening)}</NumCell>
                      <NumCell>{money(r.inflows)}</NumCell>
                      <NumCell>{money(r.outflows)}</NumCell>
                      <NumCell>{money(r.closing)}</NumCell>
                      <td>
                        <DayBadges flags={r.flags} />
                      </td>
                    </tr>
                    {open === key && (
                      <tr>
                        <td colSpan={6} className="bg-paper">
                          <ul className="flex flex-col gap-1 text-sm">
                            {items.map((it, i) => (
                              <li
                                key={`${it.sourceType}:${it.sourceId}:${i}`}
                                className="flex flex-wrap items-center gap-2"
                              >
                                <Badge tone={BASIS_TONE[it.basis]}>
                                  {t(`finance.flow.basis.${it.basis}`)}
                                </Badge>
                                {it.sourceType === 'payables.installment' ? (
                                  <a
                                    className="underline"
                                    href={`/finance/payables?open=${p.origins[it.sourceId] ?? ''}`}
                                  >
                                    {it.label}
                                  </a>
                                ) : (
                                  <span>{it.label}</span>
                                )}
                                <span className="text-muted">
                                  {it.accountKey
                                    ? (accountName.get(it.accountKey) ?? '—')
                                    : t('finance.flow.noAccount')}
                                  {it.basis === 'overdue' &&
                                    ` · ${t('finance.flow.dueOn', { date: fmt.date(it.date) })}`}
                                </span>
                                <span className="ml-auto font-mono">{money(it.amount)}</span>
                              </li>
                            ))}
                          </ul>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </Table>
          </Panel>
        );
      })}
      <Snapshots
        entityId={entity.legalEntityId}
        onSnapshot={() => snapshot.mutate()}
        saving={snapshot.isPending}
        error={snapshot.error}
      />
    </>
  );
}

/**
 * Visão consolidada (D-08): os totais de todas as moedas convertidos para uma só, na exibição,
 * pela taxa do dia de cada moeda, com a taxa, a data e a fonte. Moeda sem taxa fica fora do total.
 */
function Consolidated({
  projection: p,
  currencies,
  target,
  onTarget,
  onlyMoving,
}: {
  projection: CashProjection;
  currencies: string[];
  target: string;
  onTarget: (currency: string) => void;
  onlyMoving: boolean;
}) {
  const { t, i18n } = useTranslation();
  const fmt = useFormat();
  const c = p.consolidation;
  const money = (v: string | null) => fmt.money(v, c.currency);
  const options = [...new Set(['BRL', 'USD', target, ...currencies])].sort();
  const rateText = (r: CashProjection['consolidation']['rates'][number]) =>
    t('finance.flow.consolidated.rate', {
      from: r.inverse ? c.currency : r.currency,
      to: r.inverse ? r.currency : c.currency,
      rate: Number(r.rate).toLocaleString(i18n.language, { maximumFractionDigits: 6 }),
      date: fmt.date(r.rateDate),
      source: r.source ? t(`finance.flow.consolidated.sources.${r.source}`, r.source) : '—',
    });
  const rows = c.totals;
  const visible = onlyMoving
    ? rows.filter((r) => r.date === p.asOf || r.inflows !== '0.0000' || r.outflows !== '0.0000')
    : rows;
  const today = rows[0];
  return (
    <Panel title={t('finance.flow.consolidated.title', { currency: c.currency })}>
      <div className="flex flex-wrap items-center gap-3">
        <Select
          id="flow-consolidation"
          aria-label={t('finance.flow.fields.consolidationCurrency')}
          value={target}
          onChange={(e) => onTarget(e.target.value)}
        >
          {options.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </Select>
        <p className="text-sm text-muted">{t('finance.flow.consolidated.hint')}</p>
      </div>
      {c.rates.length > 0 && (
        <ul className="flex flex-col gap-1 text-sm">
          {c.rates.map((r) => (
            <li key={r.currency}>{rateText(r)}</li>
          ))}
        </ul>
      )}
      {c.missing.length > 0 && (
        <p className="text-sm text-muted">
          {t('finance.flow.consolidated.missing', { currencies: c.missing.join(', ') })}
        </p>
      )}
      {today && c.summary && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <StatCard label={t('finance.flow.todayBalance')} value={money(today.closing)} />
          <StatCard
            label={t('finance.flow.lowest')}
            value={money(c.summary.lowest.closing)}
            detail={fmt.date(c.summary.lowest.date)}
          />
          <StatCard
            label={t('finance.flow.firstBelowMinimum')}
            value={c.summary.firstBelowMinimum ? fmt.date(c.summary.firstBelowMinimum) : '—'}
            detail={
              c.summary.firstNegative
                ? t('finance.flow.firstNegative', { date: fmt.date(c.summary.firstNegative) })
                : undefined
            }
          />
        </div>
      )}
      <Table
        head={[
          t('finance.flow.fields.date'),
          { label: t('finance.flow.fields.inflows'), align: 'right' },
          { label: t('finance.flow.fields.outflows'), align: 'right' },
          { label: t('finance.flow.fields.closing'), align: 'right' },
          t('finance.flow.fields.situation'),
        ]}
      >
        {visible.length === 0 && <EmptyRow colSpan={5}>{t('finance.flow.nothing')}</EmptyRow>}
        {visible.map((r) => (
          <tr key={r.date}>
            <td className="whitespace-nowrap">
              {fmt.date(r.date)}
              {r.date === p.asOf && (
                <div className="text-xs text-muted">{t('finance.flow.today')}</div>
              )}
            </td>
            <NumCell>{money(r.inflows)}</NumCell>
            <NumCell>{money(r.outflows)}</NumCell>
            <NumCell>{money(r.closing)}</NumCell>
            <td>
              <DayBadges flags={r.flags} />
            </td>
          </tr>
        ))}
      </Table>
    </Panel>
  );
}

/** Saldo inicial do primeiro dia: o final menos o que entrou e mais o que saiu nele. */
function openingOfFirst(r: ProjectionTotal): string {
  return fromScaled(toScaled(r.closing) - toScaled(r.inflows) + toScaled(r.outflows));
}

function DayBadges({ flags }: { flags: ProjectionTotal['flags'] }) {
  const { t } = useTranslation();
  if (flags.length === 0) return <Badge tone="ok">{t('finance.flow.flags.ok')}</Badge>;
  return (
    <div className="flex flex-wrap gap-1">
      {flags.map((f) => (
        <Badge
          key={f}
          tone={f === 'negative' ? 'off' : f === 'below_minimum' ? 'accent' : 'neutral'}
        >
          {t(`finance.flow.flags.${f}`)}
        </Badge>
      ))}
    </div>
  );
}

function Snapshots({
  entityId,
  onSnapshot,
  saving,
  error,
}: {
  entityId: string;
  onSnapshot: () => void;
  saving: boolean;
  error: unknown;
}) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const data = useQuery({
    queryKey: [...KEY, 'snapshots', entityId],
    queryFn: () =>
      api.get<{ snapshots: CashSnapshot[] }>(
        `/finance/treasury/projection/snapshots?legalEntityId=${entityId}`,
      ),
  });
  return (
    <Panel
      title={t('finance.flow.snapshotsTitle')}
      action={
        <Button variant="secondary" disabled={saving} onClick={onSnapshot}>
          {t('finance.flow.takeSnapshot')}
        </Button>
      }
    >
      <p className="text-sm text-muted">{t('finance.flow.snapshotsHint')}</p>
      <ErrorBox error={error} />
      {data.isPending ? (
        <Loading />
      ) : data.isError ? (
        <ErrorBox error={data.error} />
      ) : (
        <Table
          head={[
            t('finance.flow.fields.asOf'),
            t('finance.flow.fields.horizonEnd'),
            t('finance.flow.fields.kind'),
            t('finance.flow.fields.recordedAt'),
          ]}
        >
          {data.data.snapshots.length === 0 && (
            <EmptyRow colSpan={4}>{t('finance.flow.noSnapshots')}</EmptyRow>
          )}
          {data.data.snapshots.slice(0, 10).map((s) => (
            <tr key={s.id}>
              <td>{fmt.date(s.asOf)}</td>
              <td>{fmt.date(s.horizonEnd)}</td>
              <td>
                <Badge tone={s.kind === 'daily' ? 'info' : 'neutral'}>
                  {t(`finance.flow.snapshotKinds.${s.kind}`)}
                </Badge>
              </td>
              <td className="text-xs">
                {fmt.dateTime(s.createdAt)}
                {s.createdByName && ` · ${s.createdByName}`}
              </td>
            </tr>
          ))}
        </Table>
      )}
    </Panel>
  );
}

function SettingsForm({ entity, onDone }: { entity: CashSettingsEntity; onDone: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: (body: unknown) =>
      api.put(`/finance/treasury/settings/${entity.legalEntityId}`, body),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: KEY });
    },
  });
  const [start] = useState(() => ({
    minimumBalance: entity.settings?.minimumBalance.replace(/\.?0+$/, '') ?? '',
    minimumCurrency: entity.settings?.minimumCurrency ?? entity.functionalCurrency,
    consolidationCurrency: entity.settings?.consolidationCurrency ?? 'BRL',
  }));
  const [f, setF] = useState(start);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) =>
    setF((s) => ({ ...s, [k]: e.target.value }));
  return (
    <FormModal
      open
      size="md"
      title={t('finance.flow.parameters')}
      description={t('finance.flow.parametersHint', { entity: `${entity.code} · ${entity.name}` })}
      dirty={changed(start, f)}
      saving={save.isPending}
      error={save.error}
      onSave={() =>
        save.mutate(
          {
            minimumBalance: normalizeDecimal(f.minimumBalance),
            minimumCurrency: f.minimumCurrency.trim().toUpperCase(),
            consolidationCurrency: f.consolidationCurrency.trim().toUpperCase(),
          },
          { onSuccess: onDone },
        )
      }
      onClose={onDone}
    >
      <FormSection>
        <Field label={t('finance.flow.fields.minimumBalance')} required>
          <Input
            id="flow-min"
            inputMode="decimal"
            required
            value={f.minimumBalance}
            onChange={set('minimumBalance')}
          />
        </Field>
        <Field label={t('finance.flow.fields.minimumCurrency')} required>
          <CurrencySelect
            id="flow-min-cur"
            value={f.minimumCurrency}
            onChange={(code) => setF((s) => ({ ...s, minimumCurrency: code }))}
          />
        </Field>
        <Field
          label={t('finance.flow.fields.consolidationCurrency')}
          hint={t('finance.flow.consolidationHint')}
          required
        >
          <CurrencySelect
            id="flow-cons-cur"
            value={f.consolidationCurrency}
            onChange={(code) => setF((s) => ({ ...s, consolidationCurrency: code }))}
          />
        </Field>
      </FormSection>
    </FormModal>
  );
}
